import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createTicket, uploadTicketAttachment } from '../api/tickets';
import type { CreateTicketPayload } from '../api/tickets';
import { CATEGORY_RELATED_TYPE } from '../types/ticket';
import type { TicketCategory, TicketAttachment } from '../types/ticket';
import { listProjects, listEpisodes, listShots } from '../api';
import type { Project, Episode, Shot } from '../types';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { useImageAspectRatioGuard } from '../hooks/useImageAspectRatioGuard';
import { tosAccelerate } from '../utils/tosAccelerate';

function AttachmentThumb({ att }: { att: TicketAttachment }) {
  const { t } = useTranslation('tickets');
  const baseStyle: React.CSSProperties = {
    width: 80,
    height: 80,
    borderRadius: 'var(--radius-sm)',
    background: 'var(--surface-2)',
    color: 'var(--text-3)',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--border)',
  };

  if (att.type === 'image') {
    return (
      <img
        src={tosAccelerate(att.url)}
        alt={att.fileName}
        style={{ ...baseStyle, objectFit: 'cover', background: 'var(--surface-2)' }}
      />
    );
  }
  if (att.type === 'video') {
    return <div style={baseStyle}>{t('createTicket.videoThumb')}</div>;
  }
  return <div style={baseStyle}>{t('createTicket.fileThumb')}</div>;
}

const CATEGORY_KEYS: TicketCategory[] = [
  'script_upload', 'episode_processing', 'asset_analysis', 'base_image',
  'shot_generation', 'state_image_prompt', 'video_generation', 'account_credits', 'other',
];

export default function CreateTicket() {
  const { t } = useTranslation('tickets');
  const navigate = useNavigate();
  const toast = useToast();
  const guardAspectRatio = useImageAspectRatioGuard();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<TicketCategory>('other');
  const [content, setContent] = useState('');
  const [projectId, setProjectId] = useState('');
  const [episodeId, setEpisodeId] = useState('');
  const [shotId, setShotId] = useState('');
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectPage, setProjectPage] = useState(0);
  const [projectTotal, setProjectTotal] = useState(0);
  const [loadingMoreProjects, setLoadingMoreProjects] = useState(false);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [shots, setShots] = useState<Shot[]>([]);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);

  const rel = CATEGORY_RELATED_TYPE[category];
  const showProject = rel === 'project' || rel === 'episode' || rel === 'shot' || rel === 'optional';
  const showEpisode = rel === 'episode' || rel === 'shot' || rel === 'optional';
  const showShot = rel === 'shot' || rel === 'optional';
  const hasMoreProjects = projects.length < projectTotal;
  const selectedProject = projects.find((p) => p.id === projectId);

  useEffect(() => {
    if (!showProject) {
      setProjects([]);
      setProjectPage(0);
      setProjectTotal(0);
      setProjectMenuOpen(false);
      return;
    }
    let cancelled = false;
    listProjects(0)
      .then((res) => {
        if (cancelled) return;
        setProjects(res.projects);
        setProjectPage(0);
        setProjectTotal(res.total);
      })
      .catch(() => toast.error(t('createTicket.loadProjectsFailed')));
    return () => {
      cancelled = true;
    };
  }, [showProject, toast, t]);

  const loadMoreProjects = async () => {
    if (loadingMoreProjects || !hasMoreProjects) return;
    const nextPage = projectPage + 1;
    setLoadingMoreProjects(true);
    try {
      const res = await listProjects(nextPage);
      setProjects((prev) => [...prev, ...res.projects]);
      setProjectPage(nextPage);
      setProjectTotal(res.total);
    } catch {
      toast.error(t('createTicket.loadProjectsFailed'));
    } finally {
      setLoadingMoreProjects(false);
    }
  };

  useEffect(() => {
    if (!projectId || !showEpisode) {
      setEpisodes([]);
      return;
    }
    listEpisodes(projectId)
      .then(setEpisodes)
      .catch(() => toast.error(t('createTicket.loadEpisodesFailed')));
  }, [projectId, showEpisode, toast, t]);

  useEffect(() => {
    if (!episodeId || !showShot) {
      setShots([]);
      return;
    }
    listShots(episodeId)
      .then(setShots)
      .catch(() => toast.error(t('createTicket.loadShotsFailed')));
  }, [episodeId, showShot, toast, t]);

  const onFiles = async (files: FileList | null) => {
    if (!files) return;
    const uploaded: TicketAttachment[] = [];
    const fileList = Array.from(files);
    for (const f of fileList) {
      // 仅对图片附件校验宽高比；视频附件不受限
      if (f.type.startsWith('image/') && !(await guardAspectRatio(f))) continue;
      try {
        const a = await uploadTicketAttachment(f);
        uploaded.push(a);
      } catch (e) {
        toast.error(t('createTicket.uploadFailed', { error: e instanceof Error ? e.message : t('createTicket.unknownError') }));
      }
    }
    if (uploaded.length > 0) {
      setAttachments((prev) => [...prev, ...uploaded]);
    }
  };

  const removeAttachment = (url: string) => {
    setAttachments((prev) => prev.filter((x) => x.url !== url));
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error(t('createTicket.titleRequired'));
      return;
    }
    if (!content.trim()) {
      toast.error(t('createTicket.contentRequired'));
      return;
    }
    if (rel === 'project' && !projectId) {
      toast.error(t('createTicket.projectRequired'));
      return;
    }
    if (rel === 'episode' && !episodeId) {
      toast.error(t('createTicket.episodeRequired'));
      return;
    }
    if (rel === 'shot' && !shotId) {
      toast.error(t('createTicket.shotRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const body: CreateTicketPayload = {
        category,
        title,
        content,
        attachments,
      };
      if (projectId) {
        body.projectId = projectId;
      }
      if (shotId) {
        body.relatedType = 'shot';
        body.relatedId = shotId;
      } else if (episodeId) {
        body.relatedType = 'episode';
        body.relatedId = episodeId;
      }
      const ticket = await createTicket(body);
      toast.success(t('createTicket.submitted'));
      navigate(`/tickets/${ticket.id}`);
    } catch (e) {
      toast.error(t('createTicket.submitFailed', { error: e instanceof Error ? e.message : t('createTicket.unknownError') }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container ticket-create-page">
      <Breadcrumb
        items={[
          { label: t('createTicket.breadcrumbHome'), to: '/projects' },
          { label: t('createTicket.breadcrumbMyTickets'), to: '/tickets' },
          { label: t('createTicket.breadcrumbTitle') },
        ]}
      />
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('createTicket.pageTitle')}</h1>
          <p className="tickets-page-subtitle">{t('createTicket.pageSubtitle')}</p>
        </div>
      </div>

      <div className="ticket-form-card">
        <div className="ticket-form-section">
          <div className="ticket-form-section-header">
            <h2>{t('createTicket.sectionIssue')}</h2>
            <p>{t('createTicket.sectionIssueDesc')}</p>
          </div>

          <div className="ticket-form-grid">
            <div>
              <label htmlFor="title-input" className="ticket-field-label">
              {t('createTicket.titleLabel')}
            </label>
            <input
              id="title-input"
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              placeholder={t('createTicket.titlePlaceholder')}
              style={{ width: '100%' }}
            />
            </div>

            <div>
              <label htmlFor="category-sel" className="ticket-field-label">
                {t('createTicket.categoryLabel')}
              </label>
              <select
                id="category-sel"
                aria-label={t('createTicket.categoryLabel')}
                className="input"
                value={category}
                onChange={(e) => setCategory(e.target.value as TicketCategory)}
                style={{ width: '100%' }}
              >
                {CATEGORY_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {t(`category.${k}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="ticket-form-section">
          <div className="ticket-form-section-header">
            <h2>{t('createTicket.sectionContext')}</h2>
            <p>{t('createTicket.sectionContextDesc')}</p>
          </div>

          <div className="ticket-form-grid">
            {showProject && (
              <div>
                <label htmlFor="project-sel" className="ticket-field-label">
                  {t('createTicket.projectLabel')}
                </label>
                <div className="ticket-project-select">
                  <button
                    type="button"
                    id="project-sel"
                    aria-label={t('createTicket.projectLabel')}
                    aria-haspopup="listbox"
                    aria-expanded={projectMenuOpen}
                    className="input ticket-project-trigger"
                    onClick={() => setProjectMenuOpen((open) => !open)}
                  >
                    <span>{selectedProject?.name || t('createTicket.selectPlaceholder')}</span>
                    <span aria-hidden="true" className="ticket-project-trigger-icon">⌄</span>
                  </button>
                  {projectMenuOpen && (
                    <div className="ticket-project-menu" role="listbox" aria-label={t('createTicket.projectLabel')}>
                      {projects.length === 0 ? (
                        <div className="ticket-project-empty">{t('createTicket.selectPlaceholder')}</div>
                      ) : (
                        projects.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            className={`ticket-project-option${projectId === p.id ? ' ticket-project-option-selected' : ''}`}
                            role="option"
                            aria-selected={projectId === p.id}
                            onClick={() => {
                              setProjectId(p.id);
                              setEpisodeId('');
                              setShotId('');
                              setProjectMenuOpen(false);
                            }}
                          >
                            {p.name}
                          </button>
                        ))
                      )}
                      {hasMoreProjects && (
                        <button
                          type="button"
                          className="ticket-project-load-more"
                          onClick={loadMoreProjects}
                          disabled={loadingMoreProjects}
                        >
                          {loadingMoreProjects ? t('createTicket.loadingMoreProjects') : t('createTicket.loadMoreProjects')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {showEpisode && (
              <div>
                <label htmlFor="episode-sel" className="ticket-field-label">
                  {t('createTicket.episodeLabel')}
                </label>
                <select
                  id="episode-sel"
                  aria-label={t('createTicket.episodeLabel')}
                  className="input"
                  value={episodeId}
                  onChange={(e) => {
                    setEpisodeId(e.target.value);
                    setShotId('');
                  }}
                  style={{ width: '100%' }}
                  disabled={!projectId}
                >
                  <option value="">{t('createTicket.selectPlaceholder')}</option>
                  {episodes.map((ep) => (
                    <option key={ep.id} value={ep.id}>
                      {`EP${String(ep.index).padStart(2, '0')} ${ep.name}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {showShot && (
              <div>
                <label htmlFor="shot-sel" className="ticket-field-label">
                  {t('createTicket.shotLabel')}
                </label>
                <select
                  id="shot-sel"
                  aria-label={t('createTicket.shotLabel')}
                  className="input"
                  value={shotId}
                  onChange={(e) => setShotId(e.target.value)}
                  style={{ width: '100%' }}
                  disabled={!episodeId}
                >
                  <option value="">{t('createTicket.selectPlaceholder')}</option>
                  {shots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {t('createTicket.shotDisplay', { index: s.index })}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="ticket-form-section">
          <div className="ticket-form-section-header">
            <h2>{t('createTicket.sectionDetails')}</h2>
            <p>{t('createTicket.sectionDetailsDesc')}</p>
          </div>

          <div>
            <label htmlFor="content-input" className="ticket-field-label">
              {t('createTicket.descLabel')}
            </label>
            <textarea
              id="content-input"
              className="input"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={5000}
              rows={6}
              placeholder={t('createTicket.descPlaceholder')}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>
        </div>

        <div className="ticket-form-section">
          <div className="ticket-form-section-header">
            <h2>{t('createTicket.sectionAttachments')}</h2>
            <p>{t('createTicket.sectionAttachmentsDesc')}</p>
          </div>

          <div className="ticket-upload-zone">
            <div>
              <div className="ticket-upload-label">{t('createTicket.attachLabel')}</div>
              <p>{t('createTicket.attachHint')}</p>
            </div>
            <label htmlFor="attach-input" className="ticket-upload-button">
              {t('createTicket.chooseFiles')}
            </label>
            <input
              id="attach-input"
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp,video/mp4,video/quicktime"
              onChange={(e) => onFiles(e.target.files)}
              className="ticket-upload-input-hidden"
            />
          </div>

          {attachments.length > 0 && (
            <div className="ticket-attachments-grid">
              {attachments.map((a, idx) => (
                <div key={`${a.url}-${idx}`} style={{ position: 'relative' }}>
                  <AttachmentThumb att={a} />
                  <button
                    type="button"
                    onClick={() => removeAttachment(a.url)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      background: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '0 var(--radius-sm) 0 var(--radius-sm)',
                      width: 20,
                      height: 20,
                      cursor: 'pointer',
                      lineHeight: 1,
                    }}
                    aria-label={t('createTicket.removeAria', { name: a.fileName })}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

          <div className="ticket-form-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => navigate('/tickets')}
              disabled={submitting}
            >
              {t('createTicket.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={submit}
              disabled={submitting}
            >
              {submitting ? t('createTicket.submitting') : t('createTicket.submit')}
            </button>
          </div>
      </div>
    </div>
  );
}
